'use client';

import { Footer } from '@/components/public/footer';
import { NavPreview } from '@/components/public/nav-preview';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
import { ArrowRight, ChevronLeft, ChevronRight, Clock, UtensilsCrossed } from 'lucide-react';
import Image from 'next/image';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslations } from 'next-intl';
import { FaqAccordion } from '@/components/public/faq-accordion';
import { FAQ_CATEGORIES } from '@/lib/faq-content';
import { useFaqItems } from '@/lib/faq-hooks';

const diningGalleryImageSrcs = [
  '/design-images/uploads/1771421590042-0107ds1_9564.jpg',
  '/design-images/uploads/1771421590148-0118ds1_9594.jpg',
  '/design-images/uploads/1771421590292-0143ds1_9663.jpg',
  '/design-images/uploads/1771503411571-sc_16.jpg',
  '/design-images/uploads/1771503411803-sc_08.jpg',
  '/design-images/uploads/1771503412029-0012ds1_9244.jpg',
  '/design-images/uploads/1771503664882-0072ds1_9462.jpg',
  '/design-images/uploads/1771503665010-0091ds1_9522.jpg',
  '/design-images/uploads/1771503992789-ray04706.jpg',
  '/design-images/uploads/1771503992893-ray04714.jpg',
  '/design-images/uploads/1771503992979-ray04716.jpg',
  '/design-images/uploads/1771503993079-ray04717.jpg',
  '/design-images/uploads/1771504261331-ds1_2273.jpg',
  '/design-images/uploads/1771504261488-ds1_2284.jpg',
  '/design-images/uploads/1771504261620-ds1_2576.jpg',
  '/design-images/uploads/1771504443791-sc_17.jpg',
  '/design-images/uploads/1771504517200-sc_13.jpg',
  '/design-images/uploads/1771504517389-sc_08.jpg',
];

function DiningGalleryCarousel() {
  const t = useTranslations('design.dining');
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    skipSnaps: false,
    dragFree: false
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  // Let the partially-visible neighbouring slides act as prev/next targets:
  // clicking a peeking image scrolls straight to it (loop mode picks the
  // shortest direction). A small pointer-travel guard keeps a drag from
  // registering as a click.
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);
  const handleSlidePointerDown = useCallback((e: React.PointerEvent) => {
    pointerDownPos.current = { x: e.clientX, y: e.clientY };
  }, []);
  const handleSlideClick = useCallback(
    (e: React.MouseEvent, index: number) => {
      const start = pointerDownPos.current;
      if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > 10) return;
      emblaApi?.scrollTo(index);
    },
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className="relative observe-me fade-in">
      {/* Main Carousel Container */}
      <div className="overflow-hidden rounded-3xl" ref={emblaRef}>
        <div className="flex touch-pan-y">
          {diningGalleryImageSrcs.map((src, index) => (
            <div
              key={index}
              className="flex-[0_0_100%] min-w-0 md:flex-[0_0_85%] lg:flex-[0_0_70%] md:px-4"
            >
              <button
                type="button"
                onPointerDown={handleSlidePointerDown}
                onClick={(e) => handleSlideClick(e, index)}
                aria-label={t('gallery.goToImage', { number: index + 1 })}
                className="relative group block w-full rounded-2xl overflow-hidden shadow-2xl cursor-pointer"
              >
                <Image
                  src={src}
                  alt={t('gallery.imageAlt', { number: index + 1 })}
                  width={1200}
                  height={800}
                  className="w-full md:h-[600px] lg:h-[700px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Animated gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#801515]/70 via-[#801515]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={scrollPrev}
        disabled={!canScrollPrev}
        className="absolute left-4 top-[58%] md:top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-white/90 backdrop-blur-sm shadow-xl hover:bg-[#801515] hover:scale-110 transition-all duration-300 flex items-center justify-center group opacity-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/90 disabled:hover:scale-100"
        aria-label={t('gallery.previous')}
      >
        <ChevronLeft className="w-7 h-7 text-gray-900 group-hover:text-white transition-colors" />
      </button>

      <button
        onClick={scrollNext}
        disabled={!canScrollNext}
        className="absolute right-4 top-[58%] md:top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-white/90 backdrop-blur-sm shadow-xl hover:bg-[#801515] hover:scale-110 transition-all duration-300 flex items-center justify-center group opacity-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/90 disabled:hover:scale-100"
        aria-label={t('gallery.next')}
      >
        <ChevronRight className="w-7 h-7 text-gray-900 group-hover:text-white transition-colors" />
      </button>

      {/* Elegant Dots Navigation */}
      <div className="flex items-center justify-center gap-3 mt-10">
        {diningGalleryImageSrcs.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={`transition-all duration-500 rounded-full ${
              index === selectedIndex
                ? 'w-12 h-3 bg-[#801515]'
                : 'w-3 h-3 bg-gray-300 hover:bg-[#801515]/50 hover:scale-125'
            }`}
            aria-label={t('gallery.goToImage', { number: index + 1 })}
          />
        ))}
      </div>

      {/* Image Counter */}
      <div className="text-center mt-6">
        <span className="text-lg text-gray-600 font-medium">
          {selectedIndex + 1} <span className="text-[#801515]">/</span> {diningGalleryImageSrcs.length}
        </span>
      </div>
    </div>
  );
}

function FAQAccordion() {
  const category = FAQ_CATEGORIES.find((c) => c.id === 'dining')!;
  const items = useFaqItems(category);
  return <FaqAccordion items={items} accent={category.accent} />;
}

export default function DiningPage() {
  const t = useTranslations('design.dining');
  useScrollReveal();

  return (
    <main className="min-h-screen bg-white text-gray-900">
      <style jsx global>{`
        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes breatheSlow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        .image-breathe {
          animation: breathe 8s ease-in-out infinite;
        }
        .image-breathe-slow {
          animation: breatheSlow 10s ease-in-out infinite;
        }
      `}</style>

      <NavPreview />

      {/* Hero Section - Full Width with Table Setting */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src="/design-images/uploads/1771425916867-ds1_2295.jpg"
            alt={t('hero.imageAlt')}
            className="w-full h-full object-cover image-breathe-slow"
            fill
            sizes="100vw"
            priority
          />
        </div>

        <div className="relative h-full flex flex-col items-center justify-center px-6 pt-48 lg:pt-0 text-center">
          <div className="observe-me slide-up w-full max-w-3xl px-12 py-14 md:px-20 md:py-16">
            <h1 className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" style={{ textShadow: '2px 2px 30px rgba(0, 0, 0, 0.5)' }}>
              {t('hero.title')}
            </h1>
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite' }} />
              <p className="text-base md:text-lg uppercase tracking-[0.3em] text-white/90 [text-shadow:rgba(0,0,0,1)_1px_1px_3px] md:[text-shadow:rgb(0,0,0)_1.5px_1.5px_4px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>{t('hero.subtitle')}</p>
              <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite', animationDelay: '1.5s' }} />
            </div>
            <p className="text-xl sm:text-3xl md:text-4xl text-white/95 w-fit max-w-3xl mx-auto font-light mb-0 [text-shadow:rgba(0,0,0,1)_1px_1px_3px] sm:[text-shadow:rgb(0,0,0)_1.5px_1.5px_4px] md:[text-shadow:rgb(0,0,0)_2px_2px_6px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>
              {t('hero.tagline')}
            </p>
          </div>
        </div>

      </section>

      {/* Dining Concept - Asymmetric Layout (Text 2/5, Image 3/5) */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-[#faf8f6]">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('savour.titleAccent')}</span>
              <br />
              <span className="text-gray-800 text-2xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('savour.titleSub')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('savour.p1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('savour.p2')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed">
              {t('savour.p3')}
            </p>
          </div>
        </div>

        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-right">
          <Image
            src="/design-images/dining/1770369954849-DS1_2456.jpg"
            alt={t('savour.imageAlt')}
            className="w-full h-full object-cover image-breathe hover:scale-110 transition-transform duration-700"
            fill
            sizes="60vw"
          />
        </div>
      </section>

      {/* Breakfast - Asymmetric Layout (Image 3/5, Text 2/5) */}
      <section className="grid lg:grid-cols-5 min-h-[90vh] design-section-gap rounded-3xl overflow-hidden">
        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1 observe-me slide-left">
          <Image
            src="/design-images/dining/1770372728203-RAY04753.jpg"
            alt={t('breakfast.imageAlt')}
            className="w-full h-full object-cover image-breathe hover:scale-110 transition-transform duration-700"
            fill
            sizes="60vw"
          />
        </div>

        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-white order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('breakfast.titleAccent')}</span>
              <br />
              <span className="text-gray-800 text-2xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('breakfast.titleSub')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('breakfast.p1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('breakfast.p2')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed">
              {t('breakfast.p3')}
            </p>
            <p className="text-base text-[#801515] font-medium mt-6 italic">
              {t('breakfast.included')}
            </p>
          </div>
        </div>
      </section>

      {/* Culinarium / Menu - Asymmetric Layout (Text 2/5, Image 3/5) */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#f5f3f0] to-[#faf8f6]">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('culinarium.titleAccent')}</span>
              <br />
              <span className="text-gray-800 text-2xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('culinarium.titleSub')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('culinarium.p1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('culinarium.p2')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('culinarium.p3')}
            </p>
            <div>
              <a
                href="https://menu.pyn.direct/e/castle/de974a42da0ad1acdfeae3275d08d26a/"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-[26px] py-5 xl:py-6 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl xl:text-2xl rounded-full transition-all duration-500 inline-flex items-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30"
              >
                {t('culinarium.viewMenu')}
                <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>

        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-right">
          <Image
            src="/design-images/dining/1770373382487-DS1_2235.jpg"
            alt={t('culinarium.imageAlt')}
            className="w-full h-full object-cover image-breathe hover:scale-110 transition-transform duration-700"
            fill
            sizes="60vw"
          />
        </div>
      </section>

      {/* Full-Width Restaurant Atmosphere - using the homepage dining image */}
      <section className="relative h-[46vw] overflow-hidden bg-gray-100 observe-me fade-in design-section-gap rounded-3xl">
        <Image
          src="/design-images/resized/1764859025033-DS1_2295.jpg"
          alt={t('atmosphere.imageAlt')}
          className="w-full h-full object-cover image-breathe-slow"
          fill
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-transparent to-transparent" />
      </section>

      {/* Garden - Asymmetric Layout (Image 3/5, Text 2/5) */}
      <section className="grid lg:grid-cols-5 min-h-[90vh] design-section-gap rounded-3xl overflow-hidden">
        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1 observe-me slide-left">
          <Image
            src="/design-images/dining/1770373664773-0023DS1_6457.jpg"
            alt={t('garden.imageAlt')}
            className="w-full h-full object-cover image-breathe hover:scale-110 transition-transform duration-700"
            fill
            sizes="60vw"
          />
        </div>

        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#f0f4f0] to-white order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#4A6741]">{t('garden.titleAccent')}</span>
              <br />
              <span className="text-gray-800 text-2xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('garden.titleSub')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#4A6741] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('garden.p1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed">
              {t('garden.p2')}
            </p>
          </div>
        </div>
      </section>

      {/* The Team - Asymmetric Layout (Text 2/5, Image 3/5) */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        {/* Text Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-[#faf8f6]">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('chef.titleAccent')}</span>
              <br />
              <span className="text-gray-800 text-2xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('chef.titleSub')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('chef.p1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed">
              {t('chef.p2')}
            </p>
          </div>
        </div>

        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-[#faf8f6] observe-me slide-right">
          <Image
            src="/design-images/dining/1770373804229-0006DS2_5054.jpg"
            alt={t('chef.imageAlt')}
            className="w-full h-full object-contain image-breathe hover:scale-110 transition-transform duration-700"
            fill
            sizes="60vw"
          />
        </div>
      </section>

      {/* Opening Hours Section */}
      <section className="relative py-20 lg:py-32 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 observe-me slide-up">
            <h2 className="font-poppins text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6">
              <span className="text-[#801515]">{t('openingHours.title')}</span>
            </h2>
            <div className="w-20 h-1 bg-[#801515] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {/* Breakfast */}
            <div className="observe-me slide-up flex flex-col items-center text-center" style={{ animationDelay: '0.1s' }}>
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#801515]/10 to-[#801515]/5 flex items-center justify-center mb-6 group hover:shadow-lg transition-all duration-500">
                <Clock className="w-12 h-12 text-[#801515] group-hover:scale-110 transition-transform" />
              </div>
              <h3 className="font-poppins text-2xl md:text-3xl font-semibold text-gray-900 mb-3">
                {t('openingHours.breakfast.label')}
              </h3>
              <p className="text-xl md:text-2xl text-[#801515] font-medium">
                {t('openingHours.breakfast.time')}
              </p>
            </div>

            {/* À la Carte */}
            <div className="observe-me slide-up flex flex-col items-center text-center" style={{ animationDelay: '0.2s' }}>
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#801515]/10 to-[#801515]/5 flex items-center justify-center mb-6 group hover:shadow-lg transition-all duration-500">
                <UtensilsCrossed className="w-12 h-12 text-[#801515] group-hover:scale-110 transition-transform" />
              </div>
              <h3 className="font-poppins text-2xl md:text-3xl font-semibold text-gray-900 mb-3">
                {t('openingHours.alacarte.label')}
              </h3>
              <p className="text-xl md:text-2xl text-[#801515] font-medium">
                {t('openingHours.alacarte.time')}
              </p>
            </div>

            {/* Last Order */}
            <div className="observe-me slide-up flex flex-col items-center text-center" style={{ animationDelay: '0.3s' }}>
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#801515]/10 to-[#801515]/5 flex items-center justify-center mb-6 group hover:shadow-lg transition-all duration-500">
                <Clock className="w-12 h-12 text-[#801515] group-hover:scale-110 transition-transform" />
              </div>
              <h3 className="font-poppins text-2xl md:text-3xl font-semibold text-gray-900 mb-3">
                {t('openingHours.lastorder.label')}
              </h3>
              <p className="text-xl md:text-2xl text-[#801515] font-medium">
                {t('openingHours.lastorder.time')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reservation Requirement */}
      <section className="relative py-16 lg:py-24 bg-[#faf8f6] overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center observe-me slide-up">
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            {t('openingHours.reservationRequired')}{' '}
            <a
              href="tel:+40725942787"
              className="text-[#801515] font-semibold hover:underline transition-colors duration-300"
            >
              {t('openingHours.callUs')}
            </a>
          </p>
        </div>
      </section>

      {/* Gallery Section - Interactive Carousel */}
      <section className="relative py-20 lg:py-32 bg-gradient-to-b from-white via-[#f5f3f0] to-white overflow-hidden mt-8">
        <div className="max-w-7xl mx-auto p-[1%] md:px-6">
          <div className="text-center mb-16 observe-me slide-up">
            <h2 className="font-poppins text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6">
              <span className="text-[#801515]">{t('gallery.title')}</span>
            </h2>
            <div className="w-20 h-1 bg-[#801515] mx-auto" />
          </div>

          <DiningGalleryCarousel />
        </div>

        {/* Floating decorative elements */}
        <div className="absolute top-1/4 right-10 w-4 h-4 bg-[#801515] rounded-full opacity-50" style={{ animation: 'pulse 5s ease-in-out infinite' }} />
        <div className="absolute bottom-1/3 left-16 w-3 h-3 bg-[#801515] rounded-full opacity-60" style={{ animation: 'pulse 6s ease-in-out infinite', animationDelay: '2s' }} />
      </section>

      {/* FAQ Section */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white to-[#faf8f6]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
              <span className="text-[#801515]">{t('faq.title')}</span>
              <br />
              <span className="text-gray-800 text-2xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('faq.subtitle')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto" />
          </div>

          <FAQAccordion />
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center py-32 px-6 bg-gradient-to-b from-[#faf8f6] to-white">
        <div className="max-w-4xl mx-auto text-center observe-me slide-up">
          <h2 className="font-poppins text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-bold mb-10 leading-[1.1]">
            <span className="text-[#801515]">{t('cta.titleAccent')}</span>
          </h2>
          <p className="text-2xl sm:text-3xl md:text-4xl text-gray-700 leading-relaxed mb-16">
            {t('cta.line1')}<br />
            {t('cta.line2')}
          </p>
          <div>
            <a
              href="https://menu.pyn.direct/e/castle/de974a42da0ad1acdfeae3275d08d26a/"
              target="_blank"
              rel="noopener noreferrer"
              className="group px-[26px] py-7 bg-[#801515] hover:bg-[#601010] text-white font-serif text-2xl xl:text-3xl rounded-full transition-all duration-500 inline-flex items-center gap-4 mx-auto shadow-xl hover:shadow-2xl hover:shadow-[#801515]/40 transform hover:scale-105"
            >
              {t('cta.exploreMenu')}
              <ArrowRight className="w-8 h-8 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-[#801515]/5 rounded-full blur-3xl" style={{ animation: 'breatheSlow 8s ease-in-out infinite' }} />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-[#4A6741]/5 rounded-full blur-3xl" style={{ animation: 'breatheSlow 10s ease-in-out infinite', animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/4 w-3 h-3 bg-[#801515] rounded-full opacity-40" style={{ animation: 'pulse 5s ease-in-out infinite' }} />
        <div className="absolute top-1/3 right-1/4 w-2 h-2 bg-[#4A6741] rounded-full opacity-35" style={{ animation: 'pulse 6s ease-in-out infinite', animationDelay: '1s' }} />
      </section>

      <Footer />
    </main>
  );
}
